import { filter, first } from 'lodash';

import { CourseModes } from 'bundles/enroll-common/components/StartDateString';
import type { FormattedCourse } from 'bundles/enroll-common/types/enrollSectionTypes';
import type { CourseQueryEnterpriseLegacyLearnerHomeQuery } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/CourseQueryEnterpriseLegacyLearnerHome';
import type { CourseType } from 'bundles/enterprise-legacy-learner-home/utils/courseTypeMetadataUtils';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const getButtonTextByCourseProgress = (summaryCoreProgressPercentage = 0) => {
  return summaryCoreProgressPercentage === 100 ? _t('Review') : _t('Resume');
};

const getTrackingButtonTextByCourseProgress = (summaryCoreProgressPercentage = 0) => {
  return summaryCoreProgressPercentage === 100 ? 'Review' : 'Resume';
};

const getProgressByLearnerMaterial = (learnerMaterials: $TSFixMe) => {
  return (learnerMaterials && learnerMaterials.summaryCoreProgressPercentage) || 0;
};

const getEnrolledSession = (learnerSessions: $TSFixMe) => {
  return learnerSessions && first(filter(learnerSessions, (item) => item.isEnrolled));
};

export {
  getButtonTextByCourseProgress,
  getProgressByLearnerMaterial,
  getEnrolledSession,
  getTrackingButtonTextByCourseProgress,
};

export type CourseTypeMetadata = {
  courseTypeMetadata: {
    __typename: string;
  };
};

export function isGuidedProject(courseTypeMetadata: CourseTypeMetadata | null | undefined) {
  return (
    courseTypeMetadata?.courseTypeMetadata.__typename === 'Course_RhymeProjectMetadata' ||
    courseTypeMetadata?.courseTypeMetadata.__typename === 'CourseTypeMetadataV1_rhymeProjectMember'
  );
}

export function isProject(courseTypeMetadata: CourseTypeMetadata | null | undefined) {
  return (
    courseTypeMetadata?.courseTypeMetadata.__typename === 'Course_ProjectMetadata' ||
    courseTypeMetadata?.courseTypeMetadata.__typename === 'CourseTypeMetadataV1_projectMember'
  );
}

export function isProjectOrSelfPacedProject(courseTypeMetadata: CourseTypeMetadata | null | undefined) {
  return isGuidedProject(courseTypeMetadata) || isProject(courseTypeMetadata);
}

export const getCourseType = (courseTypeMetadata: CourseTypeMetadata | null | undefined): CourseType | undefined => {
  switch (courseTypeMetadata?.courseTypeMetadata.__typename) {
    case 'Course_RhymeProjectMetadata':
    case 'CourseTypeMetadataV1_rhymeProjectMember':
      return 'GuidedProject';
    case 'Course_ProjectMetadata':
    case 'CourseTypeMetadataV1_projectMember':
      return 'Project';
    case 'Course_StandardCourseMetadata':
    case 'CourseTypeMetadataV1_standardCourseMember':
      return 'StandardCourse';
    case 'Course_VerifiedSkillsCourseMetadata':
    case 'CourseTypeMetadataV1_verifiedSkillsCourseMember':
      return 'VerifiedSkillsCourse';
    // This type is a basically a shell course with only assessments. Despite the name, it is not related to short term content
    case 'Course_ShortFormContentMetadata':
    case 'CourseTypeMetadataV1_shortFormContentMember':
      return 'Assessment';
    default:
      throw new Error(`typeName of ${courseTypeMetadata?.courseTypeMetadata.__typename} not supported.`);
  }
};

/**
 * GraphQL to CoursesV1 mapping
 */
// Is course launched
export const isCourseLaunched = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): boolean => {
  const now = Date.now();
  return courseData?.launchedAt ? new Date(courseData.launchedAt).valueOf() < now : false;
};

// Is course pre-enrolling
export const isCoursePreEnrolling = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): boolean => {
  const now = Date.now();
  const isLaunched = isCourseLaunched(courseData);
  return courseData?.preEnrollmentEnabledAt
    ? !isLaunched && new Date(courseData.preEnrollmentEnabledAt).valueOf() < now
    : false;
};

// Is course sessions enabled
export const isCourseSessionsEnabled = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): boolean => {
  const now = Date.now();
  return courseData?.sessionsEnabledAt ? new Date(courseData.sessionsEnabledAt).valueOf() < now : false;
};

// Get course status
export const getCourseStatus = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): 'preenroll' | 'draft' | 'launched' | 'inactive' => {
  const isLaunched = isCourseLaunched(courseData);
  const isPreEnrolling = isCoursePreEnrolling(courseData);
  return isLaunched && courseData?.isReal ? 'launched' : isPreEnrolling && courseData?.isReal ? 'preenroll' : 'draft';
};

// Get course type
export const getV2CourseType = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): 'v2.capstone' | 'v2.ondemand' => {
  const isCapstone = courseData?.specializations?.some((s12n: { capstoneCourses?: Array<{ id: string }> }) =>
    s12n.capstoneCourses?.some((capstone: { id: string }) => capstone.id === courseData.id)
  );
  return isCapstone ? 'v2.capstone' : 'v2.ondemand';
};

export const formatGraphqlCourse = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): Partial<FormattedCourse> | undefined => {
  if (!courseData?.id) {
    return undefined;
  }

  return {
    id: courseData?.id ?? undefined,
    name: courseData?.name ?? undefined,
    slug: courseData?.slug ?? undefined,
    upcomingSessionStartDate: courseData?.upcomingCourseSession?.startsAt
      ? new Date(courseData.upcomingCourseSession.startsAt).getTime()
      : undefined,
    isLaunched: isCourseLaunched(courseData),
    isSessionsEnabled: isCourseSessionsEnabled(courseData),
    plannedLaunchDate: courseData?.plannedLaunchDate ?? undefined,
    courseMode: courseData?.sessionsEnabledAt ? CourseModes.Session : CourseModes.OnDemand,
  };
};
